/**
 * Toast context - provides toast notification functionality throughout the app
 */

import { createContext, useContext, useState, useCallback, ReactNode } from 'react';
import { ToastContainer } from '@/components/common/ToastContainer';
import type {
  Toast,
  ToastOptions,
  ToastContextValue,
  ToastPosition,
} from '@/components/common/props/Toast.types';

const ToastContext = createContext<ToastContextValue | undefined>(undefined);

export interface ToastProviderProps {
  children: ReactNode;
  position?: ToastPosition;
  defaultDuration?: number;
}

/**
 * Toast provider component
 * Wrap your app with this to enable toast notifications
 */
export function ToastProvider({
  children,
  position = 'top-right',
  defaultDuration = 5000,
}: ToastProviderProps) {
  const [toasts, setToasts] = useState<Toast[]>([]);

  const addToast = useCallback(
    (message: string, options: ToastOptions = {}): string => {
      const id = `toast-${Date.now()}-${Math.random().toString(36).substr(2, 9)}`;
      const toast: Toast = {
        id,
        variant: options.variant || 'info',
        title: options.title,
        message,
        duration: options.duration !== undefined ? options.duration : defaultDuration,
        action: options.action,
      };

      setToasts((prev) => [...prev, toast]);
      return id;
    },
    [defaultDuration]
  );

  const removeToast = useCallback((id: string) => {
    setToasts((prev) => prev.filter((toast) => toast.id !== id));
  }, []);

  const clearAll = useCallback(() => {
    setToasts([]);
  }, []);

  // Convenience methods for different variants
  const success = useCallback(
    (message: string, options: Omit<ToastOptions, 'variant'> = {}) => {
      return addToast(message, { ...options, variant: 'success' });
    },
    [addToast]
  );

  const error = useCallback(
    (message: string, options: Omit<ToastOptions, 'variant'> = {}) => {
      return addToast(message, { ...options, variant: 'error' });
    },
    [addToast]
  );

  const warning = useCallback(
    (message: string, options: Omit<ToastOptions, 'variant'> = {}) => {
      return addToast(message, { ...options, variant: 'warning' });
    },
    [addToast]
  );

  const info = useCallback(
    (message: string, options: Omit<ToastOptions, 'variant'> = {}) => {
      return addToast(message, { ...options, variant: 'info' });
    },
    [addToast]
  );

  const value: ToastContextValue = {
    toasts,
    addToast,
    success,
    error,
    warning,
    info,
    removeToast,
    clearAll,
  };

  return (
    <ToastContext.Provider value={value}>
      {children}
      <ToastContainer toasts={toasts} onRemove={removeToast} position={position} />
    </ToastContext.Provider>
  );
}

/**
 * Hook to access toast functionality
 * @throws Error if used outside ToastProvider
 */
export function useToast(): ToastContextValue {
  const context = useContext(ToastContext);
  if (!context) {
    throw new Error('useToast must be used within a ToastProvider');
  }
  return context;
}
